import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LogIn, UserPlus, ShoppingBag, KeyRound, Building2 } from "lucide-react";
import axios from "axios";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register form
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regInviteCode, setRegInviteCode] = useState("");
  const [regPartitaIva, setRegPartitaIva] = useState("");

  // Detect if client code (starts with CL-)
  const isClientCode = regInviteCode.toUpperCase().startsWith("CL-");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      navigate(user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent/catalog" : "/catalog");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regInviteCode.trim()) {
      toast.error("Inserisci il codice invito");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-with-code`, {
        email: regEmail,
        password: regPassword,
        name: regName,
        invite_code: regInviteCode.trim().toUpperCase(),
        partita_iva: isClientCode ? regPartitaIva : null,
      }, { withCredentials: true });
      const { user } = response.data;
      toast.success(`Benvenuto, ${user.name}! Registrazione completata.`);
      window.location.href = user.role === "admin" ? "/admin" : user.role === "agent" ? "/agent/catalog" : "/catalog";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-black" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white">Triolo.Store</h1>
          </div>
          <p className="text-zinc-400">Ingrosso Alimentare Premium</p>
        </div>

        <Card className="bg-[#18181b] border-white/10">
          <Tabs defaultValue="login" className="w-full">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-2 bg-[#27272a]">
                <TabsTrigger value="login" data-testid="login-tab" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                  Accedi
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
                  Registrati
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent>
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-zinc-300">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="email@esempio.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="bg-[#27272a] border-white/10 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-zinc-300">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="********"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="bg-[#27272a] border-white/10 focus:border-amber-500"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full"></span>
                        Accesso...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <LogIn className="w-4 h-4" />
                        Accedi
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300 flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-amber-500" />
                      Codice Invito *
                    </Label>
                    <Input
                      data-testid="register-invite-code-input"
                      type="text"
                      placeholder="Es: AG-XXXXXX o CL-XXXXXX"
                      value={regInviteCode}
                      onChange={(e) => setRegInviteCode(e.target.value)}
                      required
                      className="bg-[#27272a] border-amber-500/30 focus:border-amber-500 uppercase tracking-wider font-mono"
                    />
                    <p className="text-xs text-zinc-500">
                      Richiedi il codice al tuo referente commerciale
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Nome *</Label>
                    <Input
                      data-testid="register-name-input"
                      type="text"
                      placeholder="Il tuo nome o ragione sociale"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                      className="bg-[#27272a] border-white/10 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Email *</Label>
                    <Input
                      data-testid="register-email-input"
                      type="email"
                      placeholder="email@esempio.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      className="bg-[#27272a] border-white/10 focus:border-amber-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Password *</Label>
                    <Input
                      data-testid="register-password-input"
                      type="password"
                      placeholder="********"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      className="bg-[#27272a] border-white/10 focus:border-amber-500"
                    />
                  </div>

                  {isClientCode && (
                    <div className="space-y-2">
                      <Label className="text-zinc-300 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-amber-500" />
                        Partita IVA *
                      </Label>
                      <Input
                        data-testid="register-partita-iva-input"
                        type="text"
                        placeholder="IT12345678901"
                        value={regPartitaIva}
                        onChange={(e) => setRegPartitaIva(e.target.value)}
                        required
                        className="bg-[#27272a] border-white/10 focus:border-amber-500 font-mono"
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="register-submit-btn"
                    className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full"></span>
                        Registrazione...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        Registrati
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        <p className="text-center text-zinc-500 text-sm mt-6">
          Accedi per visualizzare il catalogo prodotti
        </p>
      </div>
    </div>
  );
}
